/**
 * Canonical open-destination icons (maater-design-ui / ?test=maater-ui).
 * One function per intent - do not pick lucide icons ad hoc for these labels.
 */
import {
  AppWindow,
  Bot,
  Eye,
  Globe,
  MessageSquarePlus,
  PanelRight,
  type LucideIcon,
} from 'lucide-react'
import type { ComponentType, SVGProps } from 'react'
import { AppWindowPlus } from './app-window-plus-icon'

export { AppWindowPlus } from './app-window-plus-icon'

export type OpenDestinationId =
  | 'open'
  | 'open-in-new-tab'
  | 'preview'
  | 'open-in-agent-panel'
  | 'open-in-chat'
  | 'open-external'
  | 'preview-panel-toggle'

/** Lucide icons or brand marks (BasecampIcon, GoogleDriveIcon, …). */
export type OpenDestinationIconComponent =
  | LucideIcon
  | ComponentType<SVGProps<SVGSVGElement> & { className?: string; size?: number | string }>

export type OpenDestinationSpec = {
  id: OpenDestinationId
  label: string
  icon: LucideIcon
  /** Short agent-facing meaning */
  meaning: string
  /** Do not reuse this icon for these intents */
  mustNotReuseFor: string[]
}

const SPECS: Record<OpenDestinationId, OpenDestinationSpec> = {
  open: {
    id: 'open',
    label: 'Open',
    icon: AppWindow,
    meaning:
      'Deep-open into the tab system (type-agnostic - row/tab icons carry document kind)',
    mustNotReuseFor: ['external URLs', 'document-kind icons'],
  },
  'open-in-new-tab': {
    id: 'open-in-new-tab',
    label: 'Open in new tab',
    icon: AppWindowPlus as LucideIcon,
    meaning:
      'Force a new editor tab. Menu label + Cmd/Ctrl+click (isModClick / resolveOpenIntent; catalog click-new-tab).',
    mustNotReuseFor: ['preview pin', 'agent panel', 'Open in {External}'],
  },
  preview: {
    id: 'preview',
    label: 'Preview',
    icon: Eye,
    meaning: 'Show in the secondary preview pane; stay on the index list',
    mustNotReuseFor: ['deep open'],
  },
  'open-in-agent-panel': {
    id: 'open-in-agent-panel',
    label: 'Open in agent panel',
    icon: Bot,
    meaning:
      'Host an existing chat thread in the right agent pane (not as an editor tab). Chats sidebar / Home chats.',
    mustNotReuseFor: [
      'preview pane toggle (PanelRight)',
      'new tab',
      'Open in chat',
    ],
  },
  'open-in-chat': {
    id: 'open-in-chat',
    label: 'Open in chat',
    icon: MessageSquarePlus,
    meaning:
      'Start or focus a chat about this item (e.g. Inbox thread -> discuss with the agent). Different from where a chat is hosted.',
    mustNotReuseFor: ['agent panel host', 'preview toggle'],
  },
  'open-external': {
    id: 'open-external',
    label: 'Open in {External}',
    icon: Globe,
    meaning:
      'Leave Maater for a known host. Prefer the limb brand icon (Basecamp, Drive, …). Globe is fallback only when destination is unknown - never SquareArrowOutUpRight.',
    mustNotReuseFor: ['in-app open', 'Open in new tab'],
  },
  'preview-panel-toggle': {
    id: 'preview-panel-toggle',
    label: 'Show / hide preview pane',
    icon: PanelRight,
    meaning:
      'Toolbar chrome only - pin/unpin the secondary preview pane. Not a menu "open" destination.',
    mustNotReuseFor: ['Open in agent panel', 'menu Open in new tab'],
  },
}

/** Ordered list for gallery / docs. */
export const OPEN_DESTINATION_SPECS: OpenDestinationSpec[] = [
  SPECS.open,
  SPECS['open-in-new-tab'],
  SPECS.preview,
  SPECS['open-in-agent-panel'],
  SPECS['open-in-chat'],
  SPECS['open-external'],
  SPECS['preview-panel-toggle'],
]

export function getOpenDestinationIcon(id: OpenDestinationId): LucideIcon {
  return SPECS[id].icon
}

export function getOpenDestinationSpec(id: OpenDestinationId): OpenDestinationSpec {
  return SPECS[id]
}

/**
 * Icon for "Open in {External}" menu rows.
 * Pass the limb brand mark when known; otherwise Globe (generic leave-Maater).
 */
export function resolveOpenExternalIcon(
  brandIcon?: OpenDestinationIconComponent | null,
): OpenDestinationIconComponent {
  return brandIcon ?? SPECS['open-external'].icon
}

/** Convenience aliases used at call sites. */
export const OpenDestinationIcons = {
  open: SPECS.open.icon,
  openInNewTab: SPECS['open-in-new-tab'].icon,
  preview: SPECS.preview.icon,
  openInAgentPanel: SPECS['open-in-agent-panel'].icon,
  openInChat: SPECS['open-in-chat'].icon,
  /** Fallback only - prefer resolveOpenExternalIcon(BasecampIcon | GoogleDriveIcon). */
  openExternal: SPECS['open-external'].icon,
  previewPanelToggle: SPECS['preview-panel-toggle'].icon,
} as const
